import React from "react";
import { TrendingUp, Users, Lightbulb, Target, CircleCheck, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const benefits = [{
  title: "Guaranteed Principal Protection",
  description: "Eliminate market risk entirely. Your retirement savings are shielded from downturns, ensuring your principal remains safe while capturing upside growth potential.",
  icon: TrendingUp
}, {
  title: "Lifetime Income Bonuses",
  description: "Unlock immediate wealth with significant upfront bonuses. Convert your existing assets into a private pension that guarantees income for life, regardless of market conditions.",
  icon: Target
}, {
  title: "Strategic Tax Efficiency",
  description: "Leverage creative finance to minimize tax exposure. Our hybrid strategies are architected to keep more capital in your pocket throughout your golden years.",
  icon: Lightbulb
}, {
  title: "Dynamic Living Benefits",
  description: "Maintain control over your future. Access living benefits for health events and enjoy the flexibility to adapt as your financial needs and lifestyle evolve.",
  icon: Users
}];
export default function KeyBenefits() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="3534" id="hybrid-annuity-benefits" ref={ref} className="py-24 bg-background relative overflow-hidden dark bg-background text-foreground">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-96 h-96 bg-primary/10 rounded-full blur-3xl opacity-20" />
        <div className="absolute bottom-[-10%] left-[-5%] w-96 h-96 bg-accent/10 rounded-full blur-3xl opacity-20" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl md:text-5xl font-display font-bold text-foreground mb-6 tracking-tight">
            The Power of Hybrid Annuities
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Architecting self-made pensions with guaranteed lifetime income and downside protection. 
            Discover the pillars of creative financial security designed for the modern era.
          </p>
        </div>

        <div className="max-w-4xl mx-auto space-y-6">
          {benefits.map((benefit, index) => <div key={index} data-index={index} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`} style={{
          transitionDelay: `${index * 150}ms`
        }}>
              <Card className="group hover:border-primary/40 transition-all duration-300 overflow-hidden border-border/20 bg-white/5 backdrop-blur-md">
                <CardContent className="p-6 sm:p-8 flex flex-col sm:flex-row items-start gap-6">
                  <div className="shrink-0">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center transition-colors duration-300 shadow-sm ${index === 0 || index === 3 ? "bg-accent/10 text-accent-foreground group-hover:bg-accent-foreground group-hover:text-primary-foreground" : "bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground"}`}>
                      <benefit.icon className="w-7 h-7" />
                    </div>
                  </div>
                  <div className="flex-1 space-y-2">
                    <h3 className="text-xl font-display font-semibold text-foreground group-hover:text-primary transition-colors duration-300">
                      {benefit.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed font-sans">
                      {benefit.description}
                    </p>
                  </div>
                  <div className="hidden sm:flex items-center justify-center self-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 -translate-x-4 group-hover:translate-x-0">
                    <CircleCheck className={`w-6 h-6 ${index === 0 || index === 3 ? "text-accent-foreground" : "text-primary"}`} />
                  </div>
                </CardContent>
              </Card>
            </div>)}
        </div>

        <div className={`mt-12 text-center transition-all duration-700 delay-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <Button asChild size="lg" className="rounded-full px-10 bg-gradient-to-r from-primary to-primary/80 hover:scale-105 transition-transform">
            <Link to="/ContactPage">
              Secure Your Pension <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}